import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import QuickActionFAB from '../../components/ui/QuickActionFAB';
import CategoryFilter from './components/CategoryFilter';
import ProductSearchBar from './components/ProductSearchBar';
import ProductTable from './components/ProductTable';
import ProductDetailsPanel from './components/ProductDetailsPanel';
import BulkActionsBar from './components/BulkActionsBar';
import ProductToolbar from './components/ProductToolbar';
import RecentActivityPanel from './components/RecentActivityPanel';

const ProductCatalogManagement = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for products
  const mockProducts = [
    {
      id: 1,
      sku: 'LAP-DELL-001',
      name: 'Laptop Dell XPS 13',
      category: 'Portátiles',
      brand: 'Dell',
      model: 'XPS 13 9320',
      price: 1299.99,
      stock: 15,
      minStock: 5,
      image: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400',
      description: 'Portátil ultradelgado con procesador Intel Core i7 de 12ª generación, 16GB RAM y SSD de 512GB.',
      weight: 1.2,
      dimensions: '295.7 x 198.1 x 14.8 mm',
      color: 'Plata',
      material: 'Aluminio',
      supplier: {
        name: 'Dell Technologies',
        contact: 'ventas@dell.es'
      },
      gallery: [
        'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400',
        'https://images.unsplash.com/photo-1541807084-5c52b6b3adef?w=400'
      ],
      lastUpdated: new Date(Date.now() - 86400000)
    },
    {
      id: 2,
      sku: 'PHN-IPH-002',
      name: 'iPhone 15 Pro',
      category: 'Smartphones',
      brand: 'Apple',
      model: 'iPhone 15 Pro',
      price: 1199.00,
      stock: 8,
      minStock: 10,
      image: 'https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400',
      description: 'Smartphone premium con chip A17 Pro, cámara de 48MP y pantalla Super Retina XDR de 6.1 pulgadas.',
      weight: 0.187,
      dimensions: '146.6 x 70.6 x 8.25 mm',
      color: 'Titanio Natural',
      material: 'Titanio',
      supplier: {
        name: 'Apple España',
        contact: 'distribuidores@apple.es'
      },
      gallery: [
        'https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400'
      ],
      lastUpdated: new Date(Date.now() - 172800000)
    },
    {
      id: 3,
      sku: 'TAB-SAM-003',
      name: 'Samsung Galaxy Tab S9',
      category: 'Tablets',
      brand: 'Samsung',
      model: 'Galaxy Tab S9',
      price: 849.99,
      stock: 0,
      minStock: 5,
      image: 'https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=400',
      description: 'Tablet premium con pantalla AMOLED de 11 pulgadas, S Pen incluido y procesador Snapdragon 8 Gen 2.',
      weight: 0.498,
      dimensions: '254.3 x 165.8 x 5.9 mm',
      color: 'Grafito',
      material: 'Aluminio',
      supplier: {
        name: 'Samsung Electronics',
        contact: 'b2b@samsung.es'
      },
      gallery: [
        'https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=400'
      ],
      lastUpdated: new Date(Date.now() - 259200000)
    },
    {
      id: 4,
      sku: 'MON-LG-004',
      name: 'Monitor LG UltraWide 34"',
      category: 'Monitores',
      brand: 'LG',
      model: '34WN80C-B',
      price: 599.99,
      stock: 25,
      minStock: 8,
      image: 'https://images.unsplash.com/photo-1527443224154-c4a3942d3acf?w=400',
      description: 'Monitor ultrawide de 34 pulgadas con resolución QHD, USB-C y tecnología IPS.',
      weight: 6.4,
      dimensions: '815.9 x 364.6 x 260.1 mm',
      color: 'Negro',
      material: 'Plástico ABS',
      supplier: {
        name: 'LG Electronics',
        contact: 'comercial@lge.es'
      },
      gallery: [
        'https://images.unsplash.com/photo-1527443224154-c4a3942d3acf?w=400'
      ],
      lastUpdated: new Date(Date.now() - 345600000)
    },
    {
      id: 5,
      sku: 'KEY-LOG-005',
      name: 'Teclado Logitech MX Keys',
      category: 'Accesorios',
      brand: 'Logitech',
      model: 'MX Keys',
      price: 109.99,
      stock: 42,
      minStock: 15,
      image: 'https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=400',
      description: 'Teclado inalámbrico premium con retroiluminación inteligente y teclas de perfil bajo.',
      weight: 0.81,
      dimensions: '430 x 131 x 20.5 mm',
      color: 'Grafito',
      material: 'Plástico',
      supplier: {
        name: 'Logitech España',
        contact: 'ventas@logitech.es'
      },
      gallery: [
        'https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=400'
      ],
      lastUpdated: new Date(Date.now() - 432000000)
    }
  ];

  // Mock categories data
  const mockCategories = [
    {
      id: 'all',
      name: 'Todas las categorías',
      productCount: 1291,
      children: []
    },
    {
      id: 'computers',
      name: 'Informática',
      productCount: 456,
      children: [
        { id: 'laptops', name: 'Portátiles', productCount: 123, children: [] },
        { id: 'desktops', name: 'Ordenadores de sobremesa', productCount: 89, children: [] },
        { id: 'monitors', name: 'Monitores', productCount: 67, children: [] },
        { id: 'accessories', name: 'Accesorios', productCount: 177, children: [] }
      ]
    },
    {
      id: 'mobile',
      name: 'Móviles y Tablets',
      productCount: 234,
      children: [
        { id: 'smartphones', name: 'Smartphones', productCount: 156, children: [] },
        { id: 'tablets', name: 'Tablets', productCount: 78, children: [] }
      ]
    },
    {
      id: 'audio',
      name: 'Audio y Sonido',
      productCount: 189,
      children: [
        { id: 'headphones', name: 'Auriculares', productCount: 98, children: [] },
        { id: 'speakers', name: 'Altavoces', productCount: 56, children: [] },
        { id: 'microphones', name: 'Micrófonos', productCount: 35, children: [] }
      ]
    },
    {
      id: 'gaming',
      name: 'Gaming',
      productCount: 167,
      children: [
        { id: 'consoles', name: 'Consolas', productCount: 23, children: [] },
        { id: 'games', name: 'Videojuegos', productCount: 89, children: [] },
        { id: 'gaming-accessories', name: 'Accesorios Gaming', productCount: 55, children: [] }
      ]
    },
    {
      id: 'home',
      name: 'Hogar Inteligente',
      productCount: 245,
      children: [
        { id: 'smart-home', name: 'Domótica', productCount: 78, children: [] },
        { id: 'security', name: 'Seguridad', productCount: 45, children: [] },
        { id: 'lighting', name: 'Iluminación', productCount: 122, children: [] }
      ]
    }
  ];

  useEffect(() => {
    // Simulate loading
    setIsLoading(true);
    setTimeout(() => {
      setProducts(mockProducts);
      setFilteredProducts(mockProducts);
      setIsLoading(false);
    }, 1000);
  }, []);

  // Filter and search logic
  useEffect(() => {
    let filtered = [...products];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (selectedCategories.length > 0 && !selectedCategories.includes('all')) {
      const categoryNames = selectedCategories.map(catId => {
        const findCategory = (categories) => {
          for (const cat of categories) {
            if (cat.id === catId) return cat.name;
            if (cat.children) {
              const found = findCategory(cat.children);
              if (found) return found;
            }
          }
          return null;
        };
        return findCategory(mockCategories);
      }).filter(Boolean);

      filtered = filtered.filter(product =>
        categoryNames.some(catName => 
          product.category.toLowerCase().includes(catName.toLowerCase())
        )
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];
      
      if (sortConfig.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredProducts(filtered);
  }, [products, searchTerm, selectedCategories, sortConfig]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleAdvancedSearch = (filters) => {
    console.log('Advanced search filters:', filters);
    // Implement advanced search logic here
  };

  const handleCategoryChange = (categories) => {
    setSelectedCategories(categories);
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSearchTerm('');
  };

  const handleProductSelect = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  const handleSort = (config) => {
    setSortConfig(config);
  };

  const handleProductEdit = (productId) => {
    console.log('Edit product:', productId);
    // Navigate to edit product page or open modal
  };

  const handleProductDuplicate = (productId) => {
    const productToDuplicate = products.find(p => p.id === productId);
    if (productToDuplicate) {
      const newProduct = {
        ...productToDuplicate,
        id: Date.now(),
        sku: `${productToDuplicate.sku}-COPY`,
        name: `${productToDuplicate.name} (Copia)`,
        lastUpdated: new Date()
      };
      setProducts(prev => [newProduct, ...prev]);
    }
  };

  const handleProductArchive = (productId) => {
    setProducts(prev => prev.filter(p => p.id !== productId));
    setSelectedProducts(prev => prev.filter(id => id !== productId));
  };

  const handleInlineEdit = (productId, field, value) => {
    setProducts(prev => prev.map(product => 
      product.id === productId 
        ? { ...product, [field]: value, lastUpdated: new Date() }
        : product
    ));
  };

  const handleBulkAction = (action) => {
    console.log('Bulk action:', action, 'on products:', selectedProducts);
    
    switch (action) {
      case 'export':
        // Export selected products
        break;
      case 'archive':
        setProducts(prev => prev.filter(p => !selectedProducts.includes(p.id)));
        setSelectedProducts([]);
        break;
      case 'update-price':
        // Open bulk price update modal
        break;
      case 'update-stock':
        // Open bulk stock update modal
        break;
      case 'change-category':
        // Open bulk category change modal
        break;
      default:
        break;
    }
  };

  const handleAddProduct = () => {
    console.log('Navigate to add product');
    // Navigate to add product page or open modal
  };

  const handleImport = (file) => {
    console.log('Import file:', file);
    // Handle file import
  };

  const handleExport = (type = 'all') => {
    console.log('Export products:', type);
    // Handle export
  };

  const handleSyncCatalog = async () => {
    console.log('Syncing with everforce.es catalog...');
    // Simulate sync process
    return new Promise(resolve => setTimeout(resolve, 2000));
  };

  const handleProductRowClick = (product) => {
    setSelectedProduct(product);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
          <BreadcrumbNavigation />
          
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-semibold text-text-primary mb-2">
              Gestión de Catálogo de Productos
            </h1>
            <p className="text-text-muted">
              Administra tu inventario de productos, precios y stock en tiempo real
            </p>
          </div>

          {/* Toolbar */}
          <ProductToolbar
            onAddProduct={handleAddProduct}
            onImport={handleImport}
            onExport={handleExport}
            onSyncCatalog={handleSyncCatalog}
            totalProducts={products.length}
            filteredProducts={filteredProducts.length}
            isLoading={isLoading}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-12 gap-6">
            {/* Left Sidebar - Filters */}
            <div className="col-span-12 lg:col-span-3 space-y-6">
              <CategoryFilter
                categories={mockCategories}
                selectedCategories={selectedCategories}
                onCategoryChange={handleCategoryChange}
                onClearFilters={handleClearFilters}
              />
              
              <ProductSearchBar
                onSearch={handleSearch}
                onAdvancedSearch={handleAdvancedSearch}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>

            {/* Center - Product Table */}
            <div className="col-span-12 lg:col-span-7">
              <ProductTable
                products={filteredProducts}
                selectedProducts={selectedProducts}
                onProductSelect={handleProductSelect}
                onSelectAll={handleSelectAll}
                onSort={handleSort}
                sortConfig={sortConfig}
                onEdit={handleProductEdit}
                onDuplicate={handleProductDuplicate}
                onArchive={handleProductArchive}
                onInlineEdit={handleInlineEdit}
                onRowClick={handleProductRowClick}
              />
            </div>

            {/* Right Sidebar - Details & Activity */}
            <div className="col-span-12 lg:col-span-2 space-y-6">
              <ProductDetailsPanel
                selectedProduct={selectedProduct}
                onClose={() => setSelectedProduct(null)}
                onEdit={handleProductEdit}
              />
              
              <div className="hidden xl:block">
                <RecentActivityPanel />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedProducts.length}
        onBulkAction={handleBulkAction}
        onClearSelection={() => setSelectedProducts([])}
      />

      {/* Quick Action FAB */}
      <QuickActionFAB />
    </div>
  );
};

export default ProductCatalogManagement;